
/**
 * Stripe plan identifiers. The names are historical and do NOT match the UI:
 * `PRO_*` is Starter, `UNLIMITED_PRO_*` is Business, `PREMIUM_PRO_*` is Pro.
 * Use `PLAN_TYPE_TO_TIER` / `getPlanLabel` rather than matching these directly.
 */
export enum PlanType {
	PRO_MONTHLY = 'PRO_MONTHLY',
	PRO_YEARLY = 'PRO_YEARLY',
	UNLIMITED_PRO_MONTHLY = 'UNLIMITED_PRO_MONTHLY',
	UNLIMITED_PRO_YEARLY = 'UNLIMITED_PRO_YEARLY',
	PREMIUM_PRO_MONTHLY = 'PREMIUM_PRO_MONTHLY',
	PREMIUM_PRO_YEARLY = 'PREMIUM_PRO_YEARLY',
	FREE = 'FREE',
}

export interface CreateCheckoutSessionPayload {
	plan_type: PlanType;
	page_id?: string;
	zone_id?: string;
	stripe_price_id?: string;
	include_add_on?: boolean;
	add_on_tier?: ExtraCreditsTier;
}
export interface ICreateCheckoutSessionResponse {
	session_id: string;
}

export interface ISCreateCheckoutSessionResponse {
	sessionId: string;
}

export interface VerifyPaymentPayload {
	session_id: string;
}

export interface IVerifyPaymentResponse {
	status: 'open' | 'expired' | 'completed';
	plan_type?: PlanType;
}

export interface ISVerifyPaymentResponse {
	status: 'open' | 'expired' | 'completed';
	planType?: PlanType;
}

export interface GetCurrentPlanPayload {
	page_id?: string;
	include_overdue_status?: boolean;
	include_payment_history?: boolean;
}

export interface ICurrentPlanResponse {
	plan_type: PlanType;
	is_overdue?: boolean;
	has_payment_history?: boolean;
	/** `reset_at` may be an ISO string or a Unix timestamp in seconds. */
	usage?: {
		used: number;
		limit: number;
		remaining: number;
		reset_at: string | number;
		/** Absent on backends that predate the breakdown. */
		breakdown?: IUsageBreakdown;
	};
	add_on_tier?: ExtraCreditsTierWire | null;
	pending_add_on_tier?: ExtraCreditsTierWire | null;
	/** Unix seconds (Stripe period fields), but tolerated as an ISO string too. */
	pending_add_on_effective_at?: string | number | null;
}

export interface ISCurrentPlanResponse {
	planType: PlanType;
	isOverdue?: boolean;
	hasPaymentHistory?: boolean;
	usage?: ISPlanUsage;
	extraCreditsTier?: ExtraCreditsTier | null;
	pendingExtraCreditsTier?: ExtraCreditsTier | null;
	pendingExtraCreditsEffectiveAt?: string | number | null;
}

export interface IGetCustomerPortalLinkResponse {
	url: string;
}

export interface UpgradePlanPayload {
	target_plan_type: PlanType;
	page_id?: string;
	zone_id?: string;
	include_add_on?: boolean;
	add_on_tier?: ExtraCreditsTier;
	/**
	 * Drops the workspace's existing extra credits as part of the upgrade.
	 * `include_add_on: false` only means "do not add one" — it does NOT remove an
	 * add-on the workspace already owns, so an upgrade that deselects it must send
	 * this explicitly.
	 */
	remove_add_on?: boolean;
}

export interface IUpgradePlanSuccessResponse {
	success: true;
	message: string;
	new_plan_type: PlanType;
}

export interface IUpgradePlanPaymentRequiredResponse {
	success: false;
	payment_url: string;
	message: string;
}

export type IUpgradePlanResponse = IUpgradePlanSuccessResponse | IUpgradePlanPaymentRequiredResponse;

// Structured (camelCase) versions
export interface ISUpgradePlanSuccessResponse {
	success: true;
	message: string;
	newPlanType: PlanType;
}

export interface ISUpgradePlanPaymentRequiredResponse {
	success: false;
	paymentUrl: string;
	message: string;
}

export type ISUpgradePlanResponse =
	| ISUpgradePlanSuccessResponse
	| ISUpgradePlanPaymentRequiredResponse;

// POST /payment/fetch-plans, called from the browser. The backend can still price by
// region, but every region currently has the same amounts.

export type FetchPlansRegion = 'US_EU' | 'OTHER';
export type FetchPlansInterval = 'MONTHLY' | 'YEARLY';

export interface IFetchPlansResponsePlan {
	plan_type: PlanType;
	stripe_price_id: string;
	amount: number;
	interval: FetchPlansInterval;
	display_label: string;
}

export interface IFetchPlansResponse {
	region: FetchPlansRegion;
	country: string | null;
	currency: string;
	plans: IFetchPlansResponsePlan[];
}

export interface ISFetchPlansResponsePlan {
	planType: PlanType;
	stripePriceId: string;
	amount: number;
	interval: FetchPlansInterval;
	displayLabel: string;
}

export interface ISFetchPlansResponse {
	region: FetchPlansRegion;
	country: string | null;
	currency: string;
	plans: ISFetchPlansResponsePlan[];
}


/** The tier's dollar value IS the monthly credits it adds. */
export const EXTRA_CREDITS_TIERS = [10, 20, 25, 50, 100, 200, 300] as const;
export type ExtraCreditsTier = (typeof EXTRA_CREDITS_TIERS)[number];

/**
 * Wire format for a tier. The API now sends plain numbers (`50`), but has also
 * shipped `"USD_50"` codes, so every tier crosses the boundary through
 * `parseExtraCreditsTier`, which accepts either. Never compare these raw.
 */
export type ExtraCreditsTierCode = `USD_${number}`;
export type ExtraCreditsTierWire = ExtraCreditsTierCode | number;

export interface IExtraCredits {
	tier: ExtraCreditsTierWire;
	monthly_allowance_dollars: number;
}

export interface ISExtraCredits {
	extraCreditsTier: ExtraCreditsTier;
	monthlyCredits: number;
	recommended: boolean;
}

export interface FetchExtraCreditsPayload {
	page_id?: string;
	/** Intended plan for the pre-checkout upsell recommendations. */
	plan_type?: PlanType;
}

export interface IFetchExtraCreditsResponse {
	/** See `ISFetchExtraCreditsResponse.canPurchase` for what this actually means. */
	can_purchase: boolean;
	recommended_tiers: ExtraCreditsTierWire[];
	add_ons: IExtraCredits[];
	current_add_on_tier?: ExtraCreditsTierWire | null;
	pending_add_on_tier?: ExtraCreditsTierWire | null;
	currency?: string;
}

export interface ISFetchExtraCreditsResponse {
	/**
	 * Whether a purchase would succeed right now. True only when ALL THREE hold
	 * (confirmed with the backend, 2026-09-19):
	 *
	 * 1. the plan is paid (Starter, Business or Pro),
	 * 2. a `payment_info` record exists,
	 * 3. the Stripe subscription status is `ACTIVE`.
	 *
	 * So it is false for Free, for a Checkout that was never completed, and for a
	 * cancelled or past-due subscription.
	 *
	 * This is NOT plan eligibility: a paid workspace whose card has lapsed reports
	 * `false`. Using it to decide "does this plan allow extra credits" tells a Business
	 * customer that extra credits need a paid plan — so eligibility is derived from
	 * `planType` and this flag only disables the purchase action.
	 */
	canPurchase: boolean;
	currentExtraCreditsTier: ExtraCreditsTier | null;
	/** A reduction/removal already scheduled for the next renewal, if any. */
	pendingExtraCreditsTier: ExtraCreditsTier | null;
	extraCredits: ISExtraCredits[];
	currency?: string;
}

export type ExtraCreditsChangeStatus = 'APPLIED' | 'SCHEDULED' | 'PAYMENT_REQUIRED';

export interface ChangeExtraCreditsPayload {
	page_id?: string;
	/** `null` removes the active extra credits. */
	add_on_tier: ExtraCreditsTier | null;
}

export interface IChangeExtraCreditsResponse {
	status: ExtraCreditsChangeStatus;
	current_add_on_tier?: ExtraCreditsTier | null;
	pending_add_on_tier?: ExtraCreditsTier | null;
	effective_at?: number | null;
	payment_url?: string | null;
}

export interface ISChangeExtraCreditsResponse {
	status: ExtraCreditsChangeStatus;
	currentExtraCreditsTier: ExtraCreditsTier | null;
	pendingExtraCreditsTier: ExtraCreditsTier | null;
	pendingEffectiveAt: string | null;
	paymentUrl: string | null;
}

export interface IExtraCreditsChargePreviewResponse {
	amount_due_cents: number;
	monthly_difference_cents: number;
	months_charged: number;
	billing_interval: FetchPlansInterval;
	renewal_at?: number | null;
}

/** What raising extra credits would charge now; mirrors the real charge exactly. */
export interface ISExtraCreditsChargePreviewResponse {
	/** Dollars, like `monthlyDifference`; both come from the same Stripe prices. */
	amountDue: number;
	monthlyDifference: number;
	/** 1 on monthly plans; months left in the billing year (incl. this one) on yearly plans. */
	monthsCharged: number;
	billingInterval: FetchPlansInterval;
	renewalAt: string | null;
}

/** Shared monthly credits (in dollars) across email, AI review, LinkedIn and offer letters. */
export interface ISPlanUsage {
	used: number;
	limit: number;
	remaining: number;
	/** ISO string or Unix timestamp in seconds, as received. */
	resetAt: string | number;
	breakdown?: IUsageBreakdown;
}

/** Dollars. `ai` includes LinkedIn lookups; the product never shows them separately. */
export interface IUsageBreakdown {
	email: number;
	ai: number;
	/** Absent on backends that predate offer letters having their own line. */
	offer?: number;
}
